import { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock, Globe, Building2, Users, Rocket, Sparkles } from "lucide-react";
import { COMPANY } from "../lib/constants";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";

const COMPANY_FACTS = [
  { icon: Users, label: "Team strength", value: "20+ engineers, designers & ops" },
  { icon: Globe, label: "Global reach", value: "India · Australia · Singapore · UAE" },
 // { icon: Building2, label: "Govt projects", value: "Kolkata Municipal Corporation" },
  { icon: Clock, label: "Response time", value: "Within 24 hrs on weekdays" },
  { icon: Rocket, label: "Active clients", value: "50+ across 11 verticals" },
  //{ icon: Sparkles, label: "Languages", value: "English · Bengali · Hindi" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-20 relative">
      <Toaster position="top-center" theme="dark" />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="orb orb-flame w-[500px] h-[500px] -top-32 -left-32 opacity-30" />
        <div className="orb orb-azure w-[600px] h-[600px] bottom-0 -right-40 opacity-30" />
        <div className="absolute inset-0 grid-bg opacity-20" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">Contact</span></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
            Let's start <span className="fb-text">something premium</span>.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 max-w-2xl text-xl text-zinc-300 leading-relaxed">
            Drop us a line, ping us on WhatsApp, or chat with our AI Flare to schedule a meeting instantly.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-12">
          {/* Form */}
          <div className="lg:col-span-3">
            <Reveal>
              <div className="gradient-border">
                <form onSubmit={onSubmit} className="rounded-3xl p-8 space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input label="Your name" name="name" value={form.name} onChange={onChange} required />
                    <Input label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                    <Input label="Phone" name="phone" value={form.phone} onChange={onChange} />
                    <Input label="Subject" name="subject" value={form.subject} onChange={onChange} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-400">Tell us about your project</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={6}
                      placeholder="What are you building? Timelines, budget, anything we should know..."
                      className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A] transition-colors"
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] via-[#FFAA33] to-[#3B82F6] text-black font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-[0_0_30px_rgba(255,107,26,0.4)]"
                  >
                    {submitting ? "Sending..." : <>Send Message <Send size={16} /></>}
                  </motion.button>
                  <p className="text-[11px] text-zinc-500 text-center pt-2">By submitting you agree to our <a href="/privacy-policy" className="text-[#3B82F6] hover:underline">Privacy Policy</a>. We never share your data.</p>
                </form>
              </div>
            </Reveal>
          </div>

          {/* Contact details column */}
          <div className="lg:col-span-2 space-y-3">
            <Reveal delay={0.05}>
              <InfoCard icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} accent="#FF6B1A" />
            </Reveal>
            <Reveal delay={0.1}>
              <InfoCard icon={Phone} label="Phone / WhatsApp" value={COMPANY.phone} href={`https://wa.me/${COMPANY.whatsapp}`} accent="#3B82F6" />
            </Reveal>
            <Reveal delay={0.15}>
              <InfoCard icon={MapPin} label="HQ" value={COMPANY.address} accent="#FFAA33" />
            </Reveal>
            <Reveal delay={0.2}>
              <InfoCard icon={Clock} label="Working hours" value="Mon–Sat · 10:00 – 19:00 IST" accent="#3B82F6" />
            </Reveal>
            <Reveal delay={0.25}>
              <div className="rounded-3xl overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1143.0730697430029!2d88.31605015528757!3d22.470126818032906!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a027b0667345ab1%3A0xc62189490974b5ac!2sCodeflare%20Labs!5e0!3m2!1sen!2sin!4v1778790819173!5m2!1sen!2sin"
                  width="500"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Company facts grid */}
        <div className="mt-20">
          <Reveal><span className="text-xs font-bold tracking-[0.25em] text-[#3B82F6] uppercase">Why work with us</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 text-3xl sm:text-4xl font-bold">A few <span className="azure-text">good reasons</span>.</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {COMPANY_FACTS.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.05}>
                <motion.div whileHover={{ y: -4 }} className="glass rounded-2xl p-6 hover:border-[#FF6B1A]/40 transition-all">
                  <f.icon className="text-[#FFAA33] mb-3" size={20} />
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{f.label}</p>
                  <p className="text-sm text-white mt-1.5">{f.value}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA strip */}
        <Reveal>
          <div className="mt-16 glass rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 orb orb-azure w-[300px] h-[300px] opacity-30" />
            <div className="relative">
              <h3 className="text-2xl font-bold">Prefer a call? Use the AI scheduler.</h3>
              <p className="text-sm text-zinc-400 mt-1">Click the chat bubble at bottom-right — Flare will collect your details and book the slot in 60 seconds.</p>
            </div>
            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="relative px-7 py-3 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold inline-flex items-center gap-2 hover:shadow-[0_0_24px_rgba(37,211,102,0.4)] transition-all">
              WhatsApp Now <Send size={14} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <input {...props} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A] transition-colors" />
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, href, accent = "#FF6B1A" }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} target={href ? "_blank" : undefined} rel="noreferrer" className="glass rounded-2xl p-5 flex items-center gap-4 hover:border-[#FF6B1A]/40 transition-colors group">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: `${accent}22`, border: `1px solid ${accent}55`, color: accent }}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</p>
        <p className="text-sm text-white">{value}</p>
      </div>
    </Wrap>
  );
}
